import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { fetchPigGroup, adjustPigGroupEstimatedWeight } from 'reducers/pigGroups/pigGroupShow';
import { setPageOptions } from 'reducers/layout';
// components
import { FormattedMessage } from 'react-intl';
import Link from 'react-router/lib/Link';
import Button from 'components/Button';
import CenterBox from 'components/CenterBox';
import StickyFooter from 'components/StickyFooter/StickyFooter';
import Preloader from 'components/Preloader';
import WeightLabeledInput from 'components/Input/WeightLabeledInput/WeightLabeledInput';
// utils
import mixpanel from 'mixpanel-browser';
import { showToastrMessage } from 'utils';
import { formatBackEndWeightToSystem, formatWeightToBackEndFormat } from 'utils/weightHelper';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
// styles
import styles from './GroupAdjustWeight.module.scss';

const getReturnPath = (from, groupId) => (
  from === 'profile' ? `/groups/${groupId}` : '/groups'
);

const GroupAdjustWeight = ({
  params: { id: paramsGroupId },
  location,
  pigGroup,
  isLoading,
  measurementSystem,
  currentUserId,
  fetchPigGroup,
  adjustPigGroupEstimatedWeight,
  setPageOptions,
  router,
}) => {
  const [weight, setWeight] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const returnPath = getReturnPath(location?.query?.from, paramsGroupId);

  useEffect(() => {
    if (pigGroup.id !== parseInt(paramsGroupId, 10)) {
      fetchPigGroup(paramsGroupId);
    }
    setPageOptions({
      mobileTitle: 'adjustWeight',
      backLink: { icon: 'fa fa-times fs-18', link: returnPath },
    });
    return () => {
      setPageOptions({ mobileTitle: '', backLink: '' });
    };
  }, []);

  // guard: if the group is not opened, do not allow adjust (defensive — should also be filtered in Groups list)
  useEffect(() => {
    if (pigGroup?.id && pigGroup?.status && pigGroup.status !== 'opened') {
      router.replace(returnPath);
    }
  }, [pigGroup?.id, pigGroup?.status]);

  const parsedWeight = parseFloat(weight);
  const isValid = weight !== '' && !Number.isNaN(parsedWeight) && parsedWeight > 0;

  const onSave = () => {
    if (!isValid || isSubmitting) return;
    const backendWeight = formatWeightToBackEndFormat(parsedWeight, measurementSystem);
    setIsSubmitting(true);
    adjustPigGroupEstimatedWeight(paramsGroupId, backendWeight)
      .then(() => {
        mixpanel.track('Feature Used', {
          user_id: currentUserId,
          feature_name: 'Groups',
          action: 'Adjust Weight',
          pig_group_id: Number(paramsGroupId),
        });
        showToastrMessage('component.toastr.groupWeight.wasUpdated');
        router.push(returnPath);
      })
      .catch((error) => {
        setIsSubmitting(false);
        toastResponseErrors(error);
      });
  };

  const showContent = pigGroup?.id === parseInt(paramsGroupId, 10);
  const currentWeightValue = formatBackEndWeightToSystem(pigGroup?.estimated_weight, measurementSystem, '');

  return (
    <CenterBox>
      <Preloader isActive={isLoading && !showContent} />
      <section className="center-box-header show-for-large">
        <div className="title">
          <FormattedMessage id="general.adjustWeight" />
        </div>
        <Link to={returnPath}><i className="fa fa-times close-center-box" /></Link>
      </section>

      {showContent && (
        <section className={`center-box-body ${styles.body}`}>
          <div className={styles.meta}>
            <div>
              <span className={styles['meta-label']}><FormattedMessage id="general.groupId" />:</span>
              <Link to={`/groups/${pigGroup.id}`} className="color-primary">{pigGroup.name}</Link>
            </div>
            {pigGroup.farm?.name && (
              <div>
                <span className={styles['meta-label']}><FormattedMessage id="general.farm" />:</span>
                <span className={styles['meta-value']}>{pigGroup.farm.name}</span>
              </div>
            )}
          </div>

          <div className={styles.field}>
            <div className={styles.label}>
              <FormattedMessage id="general.currentEstimatedAvgGroupWeight" />
            </div>
            <WeightLabeledInput
              className={styles.input}
              value={currentWeightValue}
              onChange={() => {}}
              onFocus={() => {}}
              isDisabled
            />
          </div>

          <div className={styles.field}>
            <div className={styles.label}>
              <FormattedMessage id="general.newEstimatedAvgGroupWeight" />
            </div>
            <WeightLabeledInput
              className={styles.input}
              value={weight}
              onChange={setWeight}
              onFocus={() => {}}
              hasAutoFocus
            />
          </div>
        </section>
      )}

      <section className="center-box-footer show-for-large">
        <div className="buttons text-right">
          <Link className="button margin-right-1" to={returnPath}>
            <FormattedMessage id="general.button.cancel" />
          </Link>
          <Button
            primary
            className="submit wider"
            onClick={onSave}
            disabled={!isValid || isSubmitting}
          >
            <FormattedMessage id="general.button.save" />
          </Button>
        </div>
      </section>

      <StickyFooter className="hide-for-large">
        <Button
          primary
          className="submit"
          onClick={onSave}
          disabled={!isValid || isSubmitting}
        >
          <FormattedMessage id="general.button.save" />
        </Button>
      </StickyFooter>
    </CenterBox>
  );
};

GroupAdjustWeight.propTypes = {
  params: T.object.isRequired,
  location: T.object.isRequired,
  router: T.object.isRequired,
  pigGroup: T.object,
  isLoading: T.bool.isRequired,
  measurementSystem: T.string.isRequired,
  currentUserId: T.number,
  fetchPigGroup: T.func.isRequired,
  adjustPigGroupEstimatedWeight: T.func.isRequired,
  setPageOptions: T.func.isRequired,
};

export default connect(
  (state) => ({
    pigGroup: state.pigGroups.groupShow.data,
    isLoading: state.pigGroups.groupShow.isLoading,
    measurementSystem: state.auth.user.current_company.measurement_system,
    currentUserId: state.auth.user?.id,
  }), {
    fetchPigGroup,
    adjustPigGroupEstimatedWeight,
    setPageOptions,
  }
)(GroupAdjustWeight);
